import { useEffect, useRef, useState } from "react";
import { apiRequest } from "../../services/api";
const control = "mt-1 w-full rounded-lg border bg-white p-2",
  button = "rounded-lg border bg-white px-3 py-2 disabled:opacity-40";
const today = () => new Date(Date.now() + 19800000).toISOString().slice(0, 10);
export default function SisMultiBillCorrections({
  base,
  record,
  accessToken,
  allowed,
  blocked,
  onPosted,
}) {
  const suppliers = [
      ...new Set(record.snapshot.bills.map((b) => b.supplierId)),
    ],
    registrationId = record.snapshot.registrationId;
  const [open, setOpen] = useState(false),
    [supplier, setSupplier] = useState(suppliers[0] || ""),
    [bills, setBills] = useState([]),
    [cursor, setCursor] = useState(null),
    [selected, setSelected] = useState({}),
    [total, setTotal] = useState(""),
    [date, setDate] = useState(today),
    [reason, setReason] = useState(""),
    [evidence, setEvidence] = useState(""),
    [decisionReason, setDecisionReason] = useState(""),
    [decisionType, setDecisionType] = useState("ACCEPT"),
    [preview, setPreview] = useState(null),
    [review, setReview] = useState(null),
    [history, setHistory] = useState(null),
    [checks, setChecks] = useState({}),
    [busy, setBusy] = useState(false),
    [error, setError] = useState(""),
    [notice, setNotice] = useState("");
  const alive = useRef(true),
    pending = useRef(false),
    retry = useRef(null);
  const root = `${base}/multi-bill-correction`;
  useEffect(() => {
    alive.current = true;
    return () => {
      alive.current = false;
    };
  }, []);
  function invalidate() {
    setPreview(null);
    setChecks({});
    retry.current = null;
    setError("");
    setNotice("");
  }
  function resetReview() {
    invalidate();
    setReview(null);
    setSelected({});
    setTotal("");
    setReason("");
    setDecisionReason("");
    setEvidence("");
  }
  async function run(fn) {
    if (pending.current || blocked) return;
    pending.current = true;
    setBusy(true);
    setError("");
    try {
      await fn();
    } catch (e) {
      if (alive.current)
        setError(
          e.message ||
            "Request failed; retry unchanged inputs if the result is uncertain",
        );
    } finally {
      pending.current = false;
      if (alive.current) setBusy(false);
    }
  }
  const request = (url, body) =>
    apiRequest(url, { accessToken, ...(body ? { method: "POST", body } : {}) });
  function key(stage) {
    if (retry.current?.stage !== stage)
      retry.current = { stage, id: window.crypto.randomUUID() };
    return retry.current.id;
  }
  async function loadBills(more = false) {
    await run(async () => {
      const q = new URLSearchParams({
          supplierId: supplier,
          registrationId,
          ...(more && cursor ? { after: cursor } : {}),
        }),
        r = await request(`${base}/invoices/available-bills?${q}`);
      if (alive.current) {
        setBills((old) =>
          more
            ? [
                ...old,
                ...r.bills.filter(
                  (x) =>
                    !old.some(
                      (b) =>
                        b.accrualId === x.accrualId &&
                        b.billNumber === x.billNumber,
                    ),
                ),
              ]
            : r.bills,
        );
        setCursor(r.nextCursor);
        invalidate();
        if (!more) setSelected({});
      }
    });
  }
  async function loadHistory(page = 1) {
    await run(async () => {
      const q = new URLSearchParams({
          supplierId: supplier,
          registrationId,
          page: String(page),
        }),
        r = await request(`${root}/reviews?${q}`);
      if (alive.current) setHistory(r);
    });
  }
  async function choose(id) {
    await run(async () => {
      const r = await request(`${root}/reviews/${id}`);
      if (alive.current) {
        invalidate();
        setReview(r);
        setDecisionReason("");
        setEvidence("");
      }
    });
  }
  function form() {
    return {
      supplierId: supplier,
      registrationId,
      date,
      correctedTotal: total,
      allocations: Object.values(selected).map((x) => ({
        accrualId: x.accrualId,
        billNumber: x.billNumber,
        correctedDue: x.correctedDue,
      })),
    };
  }
  async function makePreview() {
    await run(async () => {
      const r = await request(`${root}/preview`, form());
      if (alive.current) {
        invalidate();
        setPreview({ stage: "review", ...r });
      }
    });
  }
  async function retain() {
    await run(async () => {
      const r = await request(`${root}/reviews`, {
        ...form(),
        requestKey: key("review"),
        previewHash: preview.previewHash,
        reason,
        distributionReviewed: true,
        existingEvidencePreserved: true,
      });
      if (alive.current) {
        setReview({
          id: r.id,
          snapshot: r.detail.snapshot,
          previewHash: r.detail.previewHash,
          reason: r.detail.reason,
          decision: null,
          posting: null,
        });
        invalidate();
        setNotice("Review retained. No accounting entry has been posted.");
      }
    });
  }
  async function decide() {
    await run(async () => {
      const r = await request(`${root}/reviews/${review.id}/decision`, {
        requestKey: key("decision"),
        decision: decisionType,
        reason: decisionReason,
        commercialEvidenceReference: evidence,
        noFinancialPostingConfirmed: true,
      });
      if (alive.current) {
        setReview((old) => ({ ...old, decision: { id: r.id, ...r.detail } }));
        invalidate();
        setNotice(
          decisionType === "ACCEPT"
            ? "Commercial decision retained. Posting still requires a fresh preview and confirmations."
            : "Dispute retained. Nothing was posted.",
        );
      }
    });
  }
  async function postingPreview() {
    await run(async () => {
      const r = await request(
        `${root}/reviews/${review.id}/posting-preview`,
        {},
      );
      if (alive.current) {
        invalidate();
        setPreview({ stage: "post", ...r });
      }
    });
  }
  async function post() {
    await run(async () => {
      const r = await request(`${root}/reviews/${review.id}/post`, {
        requestKey: key("post"),
        previewHash: preview.previewHash,
        supplierDuesConfirmed: true,
        noTaxPaymentOrStockConfirmed: true,
        lifecycleRestrictionAccepted: true,
      });
      if (alive.current) {
        setReview((old) => ({ ...old, posting: r }));
        invalidate();
        setBills([]);
        setCursor(null);
        setHistory(null);
        setNotice(
          "Grouped correction posted. All bill adjustments committed together. Existing invoices and payments were preserved.",
        );
        try {
          await onPosted?.();
        } catch (e) {
          if (alive.current)
            setError(
              `Posting saved. Reload other accounting panels: ${e.message}`,
            );
        }
      }
    });
  }
  const usable = (b) =>
    !b.groupedCorrectionId &&
    !b.matchingBlocked &&
    !b.remainingBalanceCorrectionId &&
    (!b.acceptedAdjustment || b.acceptedAdjustment === "0.00") &&
    b.credited === "0.00";
  const check = (id, label) => (
    <label className="flex gap-2 text-sm">
      <input
        type="checkbox"
        checked={!!checks[id]}
        onChange={(e) => setChecks((s) => ({ ...s, [id]: e.target.checked }))}
      />
      {label}
    </label>
  );
  return (
    <section className="rounded-xl border border-slate-200 bg-slate-50 p-4">
      <button
        type="button"
        className="font-semibold"
        aria-expanded={open}
        onClick={() => setOpen((v) => !v)}
      >
        Grouped supplier dues corrections
      </button>
      {open && (
        <div className="mt-4 space-y-4 text-sm">
          <p>
            Same supplier, registration and open financial year only. Review →
            commercial decision → explicit posting. No input GST, payment,
            refund or stock entry. New actual invoices can match corrected dues.
            Coordinated invoice replacement requires every original bill to be
            grouped-corrected. Mixed scopes, post-correction returns, cancellation
            and repeated corrections remain blocked.
          </p>
          {error && (
            <p role="alert" className="rounded-lg bg-red-50 p-3 text-red-800">
              {error}
            </p>
          )}
          {notice && (
            <p
              role="status"
              className="rounded-lg bg-green-50 p-3 text-green-800"
            >
              {notice}
            </p>
          )}
          <fieldset disabled={busy || blocked} className="space-y-4">
            <label className="block">
              Correction supplier
              <select
                className={control}
                value={supplier}
                onChange={(e) => {
                  setSupplier(e.target.value);
                  setBills([]);
                  setCursor(null);
                  setHistory(null);
                  resetReview();
                }}
              >
                {suppliers.map((id) => (
                  <option key={id} value={id}>
                    {record.snapshot.bills.find((b) => b.supplierId === id)
                      ?.supplierName || id}
                  </option>
                ))}
              </select>
            </label>
            <div className="flex flex-wrap gap-2">
              <button
                type="button"
                className={button}
                onClick={() => loadHistory()}
              >
                Refresh correction history
              </button>
              {allowed && (
                <button
                  type="button"
                  className={button}
                  onClick={() => {
                    resetReview();
                    setBills([]);
                    setCursor(null);
                  }}
                >
                  New correction review
                </button>
              )}
            </div>
            {history && (
              <>
                <p>
                  Page {history.page} of {history.totalPages} · {history.total}{" "}
                  reviews
                </p>
                <div className="overflow-x-auto rounded-lg border bg-white">
                  <table className="w-full text-left">
                    <caption className="sr-only">
                      Grouped correction history
                    </caption>
                    <thead className="bg-slate-100">
                      <tr>
                        {[
                          "Review date",
                          "Original",
                          "Corrected",
                          "Change",
                          "Status",
                          "Action",
                        ].map((h) => (
                          <th className="p-3" key={h}>
                            {h}
                          </th>
                        ))}
                      </tr>
                    </thead>
                    <tbody>
                      {history.items.map((h) => (
                        <tr className="border-t" key={h.id}>
                          <td className="p-3">{h.date}</td>
                          <td className="p-3">₹{h.originalTotal}</td>
                          <td className="p-3">₹{h.correctedTotal}</td>
                          <td className="p-3">₹{h.difference}</td>
                          <td className="p-3">{h.status}</td>
                          <td className="p-3">
                            <button
                              type="button"
                              className={button}
                              onClick={() => choose(h.id)}
                            >
                              Open review
                            </button>
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                  {!history.items.length && (
                    <p className="p-3">No retained reviews.</p>
                  )}
                </div>
                <div className="flex gap-2">
                  <button
                    className={button}
                    type="button"
                    disabled={history.page <= 1}
                    onClick={() => loadHistory(history.page - 1)}
                  >
                    Previous correction page
                  </button>
                  <button
                    className={button}
                    type="button"
                    disabled={history.page >= history.totalPages}
                    onClick={() => loadHistory(history.page + 1)}
                  >
                    Next correction page
                  </button>
                </div>
              </>
            )}
            {!review && allowed && (
              <>
                <button
                  type="button"
                  className={button}
                  onClick={() => loadBills()}
                >
                  Load original bills
                </button>
                {!!bills.length && (
                  <div className="overflow-x-auto rounded-lg border bg-white">
                    <table className="w-full min-w-[700px] text-left">
                      <caption className="sr-only">
                        Select original bills for explicit correction
                      </caption>
                      <thead>
                        <tr>
                          {[
                            "Select",
                            "Bill",
                            "Original due",
                            "Already matched",
                            "Corrected due",
                          ].map((h) => (
                            <th key={h} className="p-3">
                              {h}
                            </th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        {bills.map((b) => {
                          const id = JSON.stringify([
                            b.accrualId,
                            b.billNumber,
                          ]);
                          return (
                            <tr key={id} className="border-t">
                              <td className="p-3">
                                <input
                                  aria-label={`Select ${b.billNumber}`}
                                  type="checkbox"
                                  disabled={
                                    !usable(b) ||
                                    (!selected[id] &&
                                      Object.keys(selected).length >= 100)
                                  }
                                  checked={!!selected[id]}
                                  onChange={(e) => {
                                    invalidate();
                                    setSelected((old) => {
                                      const next = { ...old };
                                      if (e.target.checked)
                                        next[id] = {
                                          ...b,
                                          correctedDue: b.original,
                                        };
                                      else delete next[id];
                                      return next;
                                    });
                                  }}
                                />
                              </td>
                              <td className="p-3">
                                {b.billNumber}
                                <small className="block text-amber-800">
                                  {b.correctionNotice ||
                                    (!usable(b)
                                      ? "Not eligible for this correction flow"
                                      : "")}
                                </small>
                              </td>
                              <td className="p-3">₹{b.original}</td>
                              <td className="p-3">₹{b.matched}</td>
                              <td className="p-3">
                                <input
                                  className={control}
                                  aria-label={`Corrected due ${b.billNumber}`}
                                  disabled={!selected[id]}
                                  value={selected[id]?.correctedDue || ""}
                                  inputMode="decimal"
                                  onChange={(e) => {
                                    invalidate();
                                    setSelected((old) => ({
                                      ...old,
                                      [id]: {
                                        ...old[id],
                                        correctedDue: e.target.value,
                                      },
                                    }));
                                  }}
                                />
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                )}
                {cursor && (
                  <button
                    type="button"
                    className={button}
                    onClick={() => loadBills(true)}
                  >
                    Load more original bills
                  </button>
                )}
                <div className="grid gap-3 sm:grid-cols-2">
                  <label>
                    Correction India date
                    <input
                      className={control}
                      type="date"
                      value={date}
                      onChange={(e) => {
                        invalidate();
                        setDate(e.target.value);
                      }}
                    />
                  </label>
                  <label>
                    Reviewed corrected total
                    <input
                      className={control}
                      value={total}
                      inputMode="decimal"
                      onChange={(e) => {
                        invalidate();
                        setTotal(e.target.value);
                      }}
                    />
                  </label>
                </div>
                <label className="block">
                  Distribution review reason
                  <textarea
                    className={control}
                    maxLength={1000}
                    value={reason}
                    onChange={(e) => {
                      invalidate();
                      setReason(e.target.value);
                    }}
                  />
                </label>
                <button
                  type="button"
                  className={button}
                  disabled={
                    !Object.keys(selected).length ||
                    !total ||
                    reason.trim().length < 20
                  }
                  onClick={makePreview}
                >
                  Preview bill distribution
                </button>
              </>
            )}
            {review && (
              <article className="space-y-3 rounded-lg border bg-white p-4">
                <h4 className="font-semibold">Retained review {review.id}</h4>
                <p>{review.reason}</p>
                <p>
                  Original ₹{review.snapshot.originalTotal} · Corrected ₹
                  {review.snapshot.correctedTotal} · Change ₹
                  {review.snapshot.difference} · Recorded payments ₹
                  {review.snapshot.recordedPaymentTotal}
                </p>
                <ul className="space-y-1">
                  {review.snapshot.lines.map((l) => (
                    <li key={l.accrualId + l.billNumber}>
                      {l.billNumber}: ₹{l.originalDue} → ₹{l.correctedDue} ·
                      matched ₹{l.matchedAmount} · recorded payments ₹
                      {l.recordedPaymentAmount}
                    </li>
                  ))}
                </ul>
                {review.posting ? (
                  <p role="status">
                    Posted correction {review.posting.id}. Existing
                    invoice/payment evidence preserved; unsupported follow-up
                    actions are blocked.
                  </p>
                ) : review.decision ? (
                  <>
                    <p>
                      Commercial decision: {review.decision.decision} ·{" "}
                      {review.decision.status}
                    </p>
                    <p>
                      {review.decision.reason} ·{" "}
                      {review.decision.commercialEvidenceReference}
                    </p>
                    {allowed && review.decision.decision === "ACCEPT" && (
                      <button
                        type="button"
                        className={button}
                        onClick={postingPreview}
                      >
                        Preview accounting posting
                      </button>
                    )}
                  </>
                ) : (
                  allowed && (
                    <>
                      <label className="block">
                        Commercial decision
                        <select
                          className={control}
                          value={decisionType}
                          onChange={(e) => {
                            retry.current = null;
                            setDecisionType(e.target.value);
                          }}
                        >
                          <option>ACCEPT</option>
                          <option>DISPUTE</option>
                        </select>
                      </label>
                      <label className="block">
                        Commercial decision reason
                        <textarea
                          className={control}
                          maxLength={1000}
                          value={decisionReason}
                          onChange={(e) => {
                            retry.current = null;
                            setDecisionReason(e.target.value);
                          }}
                        />
                      </label>
                      <label className="block">
                        Commercial evidence reference
                        <input
                          className={control}
                          maxLength={500}
                          value={evidence}
                          onChange={(e) => {
                            retry.current = null;
                            setEvidence(e.target.value);
                          }}
                        />
                      </label>
                      {check(
                        "decision",
                        "I understand this decision does not post accounting entries.",
                      )}
                      <button
                        type="button"
                        className={button}
                        disabled={
                          !checks.decision ||
                          decisionReason.trim().length < 20 ||
                          evidence.trim().length < 3
                        }
                        onClick={decide}
                      >
                        Save commercial decision
                      </button>
                    </>
                  )
                )}
              </article>
            )}
            {preview && (
              <article className="space-y-3 rounded-lg border border-amber-300 bg-amber-50 p-4">
                <h4 className="font-semibold">
                  {preview.stage === "post"
                    ? "Accounting posting preview"
                    : "Distribution preview — no posting"}
                </h4>
                <p>{preview.warning}</p>
                <p>
                  Original ₹{preview.snapshot.originalTotal} · Corrected ₹
                  {preview.snapshot.correctedTotal} · Change ₹
                  {preview.snapshot.difference}
                </p>
                {(preview.snapshot.postings || preview.snapshot.lines).map(
                  (l) => (
                    <p key={l.accrualId + l.billNumber}>
                      {l.billNumber} · Difference ₹{l.difference}
                      {l.proposedReferenceKind &&
                        ` · ${l.proposedReferenceKind}`}
                      {l.costLedgerId && (
                        <small className="block text-slate-600">
                          Expense:{" "}
                          {preview.ledgerNames?.[l.costLedgerId] ||
                            l.costLedgerId}{" "}
                          · Supplier liability:{" "}
                          {preview.ledgerNames?.[l.supplierLedgerId] ||
                            l.supplierLedgerId}
                        </small>
                      )}
                    </p>
                  ),
                )}
                {preview.stage === "review" ? (
                  <>
                    {check(
                      "distribution",
                      "I reviewed every explicit bill amount and the corrected total.",
                    )}
                    {check(
                      "preserve",
                      "Existing invoices and payments must remain unchanged.",
                    )}
                    <button
                      type="button"
                      className={button}
                      disabled={!checks.distribution || !checks.preserve}
                      onClick={retain}
                    >
                      Retain distribution review
                    </button>
                  </>
                ) : (
                  <>
                    {check(
                      "dues",
                      "I confirm these supplier dues adjustments.",
                    )}
                    {check(
                      "noTax",
                      "No GST claim, payment, refund or stock movement is being posted.",
                    )}
                    {check(
                      "restrictions",
                      "I accept that further returns, invoice replacement and financial corrections require a separate coordinated workflow.",
                    )}
                    <button
                      type="button"
                      className="rounded-lg bg-primary-700 px-4 py-3 text-white disabled:opacity-40"
                      disabled={
                        !checks.dues || !checks.noTax || !checks.restrictions
                      }
                      onClick={post}
                    >
                      Post all bill adjustments atomically
                    </button>
                  </>
                )}
              </article>
            )}
          </fieldset>
          {busy && <p role="status">Working…</p>}
        </div>
      )}
    </section>
  );
}
